import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useQueryClient } from '@tanstack/vue-query';
import useAuthStore from '~/stores/auth';
import { useRuntimeConfig } from '#imports';

/**
 * Portal switching, shared by the desktop user menu and the mobile navigation drawer.
 *
 * Extracted so the two surfaces cannot drift. The switch is more than a store write:
 * `switchPortal` refetches the profile under a new `X-Portal` header, so every cached
 * query has to be invalidated afterwards or the new portal renders the old portal's
 * data. Forgetting that is the failure mode this composable exists to prevent.
 */
export function usePortalSwitcher() {
   const authStore = useAuthStore();
   const { selectedPortal, availablePortals } = storeToRefs(authStore);
   const config = useRuntimeConfig();
   const queryClient = useQueryClient();

   const currentPortal = computed(() => {
      return selectedPortal.value || (config.public.portalBrand as string);
   });

   /** Single-brand builds have nothing to switch between, and neither does one portal. */
   const showPortalSwitcher = computed(() => {
      return !config.public.singleBrand && availablePortals.value.length > 1;
   });

   const switchPortal = async (portalSlug: string) => {
      if (portalSlug === currentPortal.value) {
         return;
      }

      await authStore.switchPortal(portalSlug);
      queryClient.invalidateQueries();
   };

   return {
      availablePortals,
      currentPortal,
      showPortalSwitcher,
      switchPortal,
   };
}
